/**
 * Fixed-timestep loop. Simulation ticks at a constant rate (SIM_HZ) regardless
 * of display refresh; rendering runs every animation frame and receives an
 * interpolation alpha in [0,1) between the previous and current sim state.
 *
 * This is the boundary that keeps the sim deterministic and netcode-ready:
 * nothing in `tick` may depend on wall-clock time or frame rate.
 */
export const SIM_HZ = 60;
export const SIM_DT = 1 / SIM_HZ;
const MAX_FRAME_DT = 0.25; // clamp after tab-switch so we don't spiral
const MAX_TICKS_PER_FRAME = 8;

export interface LoopCallbacks {
  /** Advance the simulation by exactly SIM_DT. */
  tick: (dt: number) => void;
  /** Draw. alpha = fraction of a sim step elapsed since the last tick. */
  render: (alpha: number, frameDt: number) => void;
}

export class FixedLoop {
  private acc = 0;
  private last = 0;
  private raf = 0;
  running = false;
  /** Multiplier applied to wall time before accumulation (slow-mo / pause). */
  timeScale = 1;
  /** Stats for the debug overlay. */
  fps = 0;
  tps = 0;
  private fpsAcc = 0;
  private fpsFrames = 0;
  private tpsTicks = 0;

  constructor(private cb: LoopCallbacks) {}

  /**
   * Keep ticking (without rendering) while the tab is hidden. Browsers pause
   * requestAnimationFrame in background tabs; online co-op sets this so a
   * player who alt-tabs doesn't freeze the lockstep for everyone. A tiny
   * Worker supplies the timer because worker timers aren't throttled like
   * background-tab timers are.
   */
  set keepAliveInBackground(on: boolean) {
    this.backgroundWanted = on;
    this.updateBackgroundTimer();
  }

  private backgroundWanted = false;
  private worker: Worker | null = null;

  private updateBackgroundTimer(): void {
    const want = this.running && this.backgroundWanted && typeof document !== 'undefined' && document.hidden;
    if (want && !this.worker && typeof Worker !== 'undefined') {
      try {
        const url = URL.createObjectURL(new Blob(['setInterval(() => postMessage(0), 20);'], { type: 'text/javascript' }));
        this.worker = new Worker(url);
        URL.revokeObjectURL(url);
        this.worker.onmessage = () => {
          if (document.hidden) this.advance(performance.now(), false);
        };
      } catch {
        this.worker = null; // CSP or no Worker support: fall back to pausing
      }
    } else if (!want && this.worker) {
      this.worker.terminate();
      this.worker = null;
    }
  }

  /** Accumulate wall time and run whole ticks; optionally render. */
  private advance(now: number, render: boolean): void {
    let frameDt = (now - this.last) / 1000;
    this.last = now;
    if (frameDt > MAX_FRAME_DT) frameDt = MAX_FRAME_DT;
    this.acc += frameDt * this.timeScale;
    let ticks = 0;
    while (this.acc >= SIM_DT && ticks < MAX_TICKS_PER_FRAME) {
      this.cb.tick(SIM_DT);
      this.acc -= SIM_DT;
      ticks++;
      this.tpsTicks++;
    }
    if (ticks === MAX_TICKS_PER_FRAME) this.acc = 0; // drop time rather than spiral
    if (!render) return;
    const alpha = this.timeScale > 0 ? this.acc / SIM_DT : 0;
    this.cb.render(alpha, frameDt);

    this.fpsAcc += frameDt;
    this.fpsFrames++;
    if (this.fpsAcc >= 0.5) {
      this.fps = this.fpsFrames / this.fpsAcc;
      this.tps = this.tpsTicks / this.fpsAcc;
      this.fpsAcc = 0;
      this.fpsFrames = 0;
      this.tpsTicks = 0;
    }
  }

  start(): void {
    if (this.running) return;
    this.running = true;
    this.last = performance.now();
    this.acc = 0;
    const frame = (now: number) => {
      if (!this.running) return;
      this.advance(now, true);
      this.raf = requestAnimationFrame(frame);
    };
    this.raf = requestAnimationFrame(frame);
    if (typeof document !== 'undefined') document.addEventListener('visibilitychange', () => this.updateBackgroundTimer());
  }

  stop(): void {
    this.running = false;
    cancelAnimationFrame(this.raf);
    this.updateBackgroundTimer();
  }
}
